import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, Search, Edit, Trash2, ArrowRight, Download, Users, User as UserIcon, X, Shield, Briefcase, MapPin, Mail, Phone, Lock } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import { Button } from '../../components/ui/Button';
import { User, Role, ReportType } from '../../types';
import { useForm, SubmitHandler } from 'react-hook-form';
import useAppStore from '../../store/useAppStore';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

type EmployeeFormInputs = Omit<User, 'id' | 'joinDate'> & { password?: string };

const ITEMS_PER_PAGE = 10;

const ModernEmployeeFormModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    employee: User | null;
    onSave: (data: any) => Promise<void>;
    isSaving: boolean;
}> = ({ isOpen, onClose, employee, onSave, isSaving }) => {
    const { register, handleSubmit, reset, formState: { errors } } = useForm<EmployeeFormInputs>();
    const { branches } = useAppStore();
    const [allowedTypes, setAllowedTypes] = useState<ReportType[]>([ReportType.Sales, ReportType.Maintenance, ReportType.Project]);

    const toggleType = (type: ReportType) => {
        setAllowedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
    };

    useEffect(() => {
        if (isOpen) {
            if (employee) {
                reset(employee);
                setAllowedTypes(employee.allowedReportTypes && employee.allowedReportTypes.length > 0
                    ? employee.allowedReportTypes
                    : [ReportType.Sales, ReportType.Maintenance, ReportType.Project]);
            } else {
                reset({ role: Role.Employee, branch: branches[0]?.name || '', department: 'الفني', employeeType: 'Technician', hasImportExportPermission: false, hasPackageManagementPermission: false, hasPurchaseManagementPermission: false as any, name: '', employeeId: '', email: '', phone: '', position: '' });
                setAllowedTypes([ReportType.Sales, ReportType.Maintenance, ReportType.Project]);
            }
        }
    }, [employee, isOpen, reset, branches]);

    const onSubmit: SubmitHandler<EmployeeFormInputs> = data => {
        if (employee) {
            onSave({ ...data, id: employee.id, allowedReportTypes: allowedTypes });
        } else {
            if (!data.password) {
                toast.error("كلمة المرور مطلوبة للموظفين الجدد.");
                return;
            }
            onSave({ ...data, allowedReportTypes: allowedTypes });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in" onClick={onClose}>
            <div className="glass-panel w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col rounded-2xl animate-scale-in" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-slate-700/50 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        {employee ? <Edit className="text-blue-400" size={24} /> : <PlusCircle className="text-emerald-400" size={24} />}
                        {employee ? 'تعديل بيانات موظف' : 'إضافة موظف جديد'}
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                    <form id="employee-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="space-y-4">
                            <h3 className="text-sm font-bold text-blue-400 uppercase tracking-wider flex items-center gap-2">
                                <UserIcon size={16} /> المعلومات الأساسية
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs text-slate-400">اسم الموظف</label>
                                    <input {...register("name", { required: "الاسم مطلوب" })} className="modern-input w-full" placeholder="الاسم الكامل" />
                                    {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>}
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs text-slate-400">رقم الموظف</label>
                                    <input {...register("employeeId", { required: "رقم الموظف مطلوب" })} className="modern-input w-full" placeholder="EMP-001" />
                                    {errors.employeeId && <p className="text-xs text-red-400 mt-1">{errors.employeeId.message}</p>}
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs text-slate-400">البريد الإلكتروني</label>
                                    <div className="relative">
                                        <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                        <input {...register("email", { required: "البريد الإلكتروني مطلوب" })} type="email" className="modern-input w-full pr-10" placeholder="email@example.com" />
                                    </div>
                                    {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email.message}</p>}
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs text-slate-400">رقم الهاتف</label>
                                    <div className="relative">
                                        <Phone className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                        <input {...register("phone")} type="tel" className="modern-input w-full pr-10" placeholder="05xxxxxxxx" dir="ltr" />
                                    </div>
                                </div>
                                {!employee && (
                                    <div className="md:col-span-2 space-y-1">
                                        <label className="text-xs text-slate-400">كلمة المرور الأولية</label>
                                        <div className="relative">
                                            <Lock className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                            <input {...register("password", { required: !employee, minLength: { value: 6, message: '6 chars min' } })} type="password" className="modern-input w-full pr-10" placeholder="******" />
                                        </div>
                                        {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password.message}</p>}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="space-y-4 pt-4 border-t border-slate-700/50">
                            <h3 className="text-sm font-bold text-blue-400 uppercase tracking-wider flex items-center gap-2">
                                <Briefcase size={16} /> معلومات العمل
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs text-slate-400">القسم</label>
                                    <input {...register("department", { required: "القسم مطلوب" })} className="modern-input w-full" />
                                    {errors.department && <p className="text-xs text-red-400 mt-1">{errors.department.message}</p>}
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs text-slate-400">المنصب</label>
                                    <input {...register("position")} className="modern-input w-full" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs text-slate-400">الفرع</label>
                                    <div className="relative">
                                        <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                        <select {...register("branch")} className="modern-input w-full pr-10 appearance-none">
                                            {branches.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs text-slate-400">نوع الموظف</label>
                                    <select {...register("employeeType")} className="modern-input w-full appearance-none">
                                        <option value="Project">موظف مشاريع</option>
                                        <option value="Accountant">محاسب</option>
                                        <option value="Technician">فني</option>
                                        <option value="Admin">إداري</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs text-slate-400">الصلاحية</label>
                                    <div className="relative">
                                        <Shield className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                        <select {...register("role")} className="modern-input w-full pr-10 appearance-none">
                                            <option value={Role.Admin}>Admin</option>
                                            <option value={Role.Employee}>Employee</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                                <label className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 border border-slate-700 cursor-pointer hover:bg-slate-800 transition-colors">
                                    <input type="checkbox" {...register("hasImportExportPermission")} className="w-5 h-5 rounded border-slate-600 text-blue-600 focus:ring-blue-500 bg-slate-700" />
                                    <span className="text-sm text-slate-300">صلاحية الاستيراد والتصدير</span>
                                </label>
                                <label className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 border border-slate-700 cursor-pointer hover:bg-slate-800 transition-colors">
                                    <input type="checkbox" {...register("hasPackageManagementPermission")} className="w-5 h-5 rounded border-slate-600 text-blue-600 focus:ring-blue-500 bg-slate-700" />
                                    <span className="text-sm text-slate-300">صلاحية إدارة البكجات</span>
                                </label>
                                <label className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 border border-slate-700 cursor-pointer hover:bg-slate-800 transition-colors">
                                    <input type="checkbox" {...register("hasPurchaseManagementPermission" as any)} className="w-5 h-5 rounded border-slate-600 text-blue-600 focus:ring-blue-500 bg-slate-700" />
                                    <span className="text-sm text-slate-300">صلاحية إدارة المشتريات</span>
                                </label>
                            </div>
                        </div>

                        <div className="space-y-4 pt-4 border-t border-slate-700/50">
                            <h3 className="text-sm font-bold text-blue-400 uppercase tracking-wider">صلاحيات أنواع التقارير</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {[ReportType.Sales, ReportType.Maintenance, ReportType.Project].map(rt => (
                                    <label key={rt} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${allowedTypes.includes(rt) ? 'bg-blue-500/10 border-blue-500/50 text-blue-400' : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:bg-slate-800'}`}>
                                        <input type="checkbox" checked={allowedTypes.includes(rt)} onChange={() => toggleType(rt)} className="hidden" />
                                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${allowedTypes.includes(rt) ? 'border-blue-500 bg-blue-500' : 'border-slate-500'}`}>
                                            {allowedTypes.includes(rt) && <Users size={12} className="text-white" />}
                                        </div>
                                        <span className="text-sm font-medium">{rt}</span>
                                    </label>
                                ))}
                            </div>
                            <button type="button" onClick={() => setAllowedTypes([ReportType.Sales, ReportType.Maintenance, ReportType.Project])} className="text-xs text-blue-400 hover:text-blue-300 underline">
                                السماح للجميع
                            </button>
                        </div>
                    </form>
                </div>

                <div className="p-6 border-t border-slate-700/50 flex justify-end gap-3 bg-slate-900/50">
                    <button onClick={onClose} disabled={isSaving} className="px-6 py-2.5 rounded-xl text-slate-400 hover:text-white font-medium transition-colors">
                        إلغاء
                    </button>
                    <button
                        type="submit"
                        form="employee-form"
                        disabled={isSaving}
                        className="px-8 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-500/25 transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {isSaving ? <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'حفظ'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ModernManageEmployeesScreen: React.FC = () => {
    const { t, user: loggedInUser, updateUser: updateLoggedInUser, toggleDesignMode } = useAppContext();
    const {
        users: employees,
        addUser,
        updateUser,
        deleteUser,
        openConfirmation,
    } = useAppStore();
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState<User | null>(null);
    const [currentPage, setCurrentPage] = useState(1);

    const handleSaveEmployee = async (data: any) => {
        setIsSaving(true);
        try {
            if (editingEmployee) {
                const payload = {
                    id: editingEmployee.id,
                    hasImportExportPermission: !!data.hasImportExportPermission,
                    hasPackageManagementPermission: !!data.hasPackageManagementPermission,
                    hasPurchaseManagementPermission: !!(data as any).hasPurchaseManagementPermission,
                    allowedReportTypes: Array.isArray(data.allowedReportTypes) ? data.allowedReportTypes : [],
                };
                const updated = await updateUser(payload);
                toast.success('تم تحديث الموظف بنجاح!');
                if (loggedInUser && editingEmployee.id === loggedInUser.id) {
                    updateLoggedInUser({
                        ...loggedInUser,
                        ...updated,
                        // Ensure boolean flags are correctly updated
                        hasImportExportPermission: !!updated.hasImportExportPermission,
                        hasPackageManagementPermission: !!updated.hasPackageManagementPermission,
                        hasPurchaseManagementPermission: !!(updated as any).hasPurchaseManagementPermission,
                    });
                }
            } else {
                await addUser(data);
                toast.success('تم إنشاء الموظف بنجاح!');
            }
            setModalOpen(false);
            setEditingEmployee(null);
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddClick = () => {
        setEditingEmployee(null);
        setModalOpen(true);
    };

    const handleEditClick = (employee: User) => {
        setEditingEmployee(employee);
        setModalOpen(true);
    };

    const handleDeleteClick = (employeeId: string) => {
        openConfirmation('هل أنت متأكد من حذف هذا الموظف؟', async () => {
            await deleteUser(employeeId);
            toast.success('تم حذف الموظف بنجاح!');
        });
    };

    const handleExport = () => {
        if (filteredEmployees) {
            const worksheet = XLSX.utils.json_to_sheet(filteredEmployees);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');
            XLSX.writeFile(workbook, 'Employees.xlsx');
            toast.success('تم تصدير البيانات بنجاح!');
        }
    };

    const filteredEmployees = useMemo(() => {
        return employees.filter(emp =>
            (emp.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (emp.employeeId || '').includes(searchTerm) ||
            (emp.branch || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [employees, searchTerm]);

    const paginatedEmployees = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredEmployees.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredEmployees, currentPage]);

    const totalPages = Math.ceil(filteredEmployees.length / ITEMS_PER_PAGE);

    return (
        <div className="space-y-8 animate-content-fade-in pb-20">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-1">{t('manageEmployees')}</h1>
                    <p className="text-slate-400">إدارة صلاحيات وبيانات الموظفين</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors font-medium border border-slate-700"
                    >
                        <Download size={18} />
                        <span>تصدير</span>
                    </button>
                    <button
                        onClick={handleAddClick}
                        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-500/25 transition-all font-medium"
                    >
                        <PlusCircle size={18} />
                        <span>إضافة موظف</span>
                    </button>
                    <button
                        onClick={toggleDesignMode}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors font-medium border border-slate-700"
                        title="رجوع إلى النسخة الكلاسيكية"
                    >
                        <ArrowRight size={16} />
                        <span>رجوع</span>
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="glass-panel rounded-2xl overflow-hidden">
                <div className="p-6 border-b border-slate-700/50 flex flex-col md:flex-row justify-between items-center gap-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Users className="text-blue-400" size={24} />
                        قائمة الموظفين
                        <span className="px-2.5 py-0.5 rounded-full bg-slate-800 text-slate-400 text-sm font-medium border border-slate-700">{filteredEmployees.length}</span>
                    </h2>
                    <div className="relative w-full md:w-72">
                        <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                            type="text"
                            placeholder="بحث باسم الموظف, الرقم, أو الفرع..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl py-2.5 pr-11 pl-4 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-right">
                        <thead className="bg-slate-800/50 text-slate-400 text-sm uppercase">
                            <tr>
                                <th className="px-6 py-4 font-medium">الاسم</th>
                                <th className="px-6 py-4 font-medium">رقم الموظف</th>
                                <th className="px-6 py-4 font-medium">الفرع</th>
                                <th className="px-6 py-4 font-medium">المنصب</th>
                                <th className="px-6 py-4 font-medium">الدور</th>
                                <th className="px-6 py-4 font-medium">إجراءات</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {paginatedEmployees.length > 0 ? (
                                paginatedEmployees.map((employee, idx) => (
                                    <tr
                                        key={employee.id}
                                        className="group hover:bg-slate-800/30 transition-colors"
                                        style={{ animationDelay: `${idx * 50}ms` }}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-slate-300 font-bold border border-slate-600">
                                                    {employee.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <div className="font-medium text-white">{employee.name}</div>
                                                    <div className="text-xs text-slate-500">{employee.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-300 font-mono text-sm">{employee.employeeId}</td>
                                        <td className="px-6 py-4 text-slate-300">{employee.branch}</td>
                                        <td className="px-6 py-4 text-slate-300">{employee.position || '-'}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${employee.role === Role.Admin ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
                                                {employee.role}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => handleEditClick(employee)}
                                                    className="p-2 rounded-lg bg-slate-800 text-blue-400 hover:bg-blue-500 hover:text-white transition-all"
                                                    title="تعديل"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteClick(employee.id)}
                                                    className="p-2 rounded-lg bg-slate-800 text-red-400 hover:bg-red-500 hover:text-white transition-all"
                                                    title="حذف"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-16 h-16 rounded-full bg-slate-800/50 flex items-center justify-center">
                                                <Users size={32} className="opacity-50" />
                                            </div>
                                            <p>لم يتم العثور على موظفين</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="p-4 border-t border-slate-700/50 flex justify-between items-center bg-slate-900/30">
                        <span className="text-sm text-slate-500">
                            عرض {paginatedEmployees.length} من {filteredEmployees.length} موظف
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 rounded-lg bg-slate-800 text-slate-400 disabled:opacity-50 hover:bg-slate-700 transition-colors"
                            >
                                السابق
                            </button>
                            <div className="flex gap-1">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${currentPage === page ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                                    >
                                        {page}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 rounded-lg bg-slate-800 text-slate-400 disabled:opacity-50 hover:bg-slate-700 transition-colors"
                            >
                                التالي
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <ModernEmployeeFormModal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                employee={editingEmployee}
                onSave={handleSaveEmployee}
                isSaving={isSaving}
            />
        </div>
    );
};

export default ModernManageEmployeesScreen;
